import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { AssignableOwnerDto, TripDto } from "@tc/shared";
import { Button, Field, Icon, Modal, Select } from "@/components/ui";
import { ApiError, tripsApi } from "@/features/trips/api";

export interface AssignOwnerModalProps {
  trip: TripDto;
  owners: AssignableOwnerDto[];
  onClose: () => void;
  /** Called after a successful assignment — the caller refreshes its data. */
  onSaved: (updated: TripDto) => void | Promise<void>;
}

/** Sentinel Select value for "no owner" (Select works in plain strings). */
const UNASSIGNED = "";

/**
 * Assign a TC operator to a trip — or hand it back to the unassigned queue (TW-3440). A
 * back-office-only action, always rendered behind a `<Can permission="trip.write">` gate by its
 * callers; the server re-checks the back-office user type and that the chosen owner is a valid
 * active back-office user.
 */
export function AssignOwnerModal({ trip, owners, onClose, onSaved }: AssignOwnerModalProps) {
  const { t } = useTranslation();
  const [ownerUserId, setOwnerUserId] = useState<string>(trip.ownerUserId ?? UNASSIGNED);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function onApply() {
    setError(null);
    setSaving(true);
    try {
      const updated = await tripsApi.assignOwner(trip.id, {
        ownerUserId: ownerUserId === UNASSIGNED ? null : ownerUserId,
      });
      await onSaved(updated);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("worklist.assignError"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open onClose={onClose} title={t("worklist.assignTitle")}>
      <div className="modal-body">
        {error && (
          <div className="auth-alert" style={{ marginBottom: 16 }} role="alert">
            <Icon name="alert" size={16} className="aa-ic" />
            <div className="aa-body">{error}</div>
          </div>
        )}
        <div className="admin-form">
          <div className="row-between">
            <span className="sub">{trip.reference}</span>
            <span className="faint" style={{ fontSize: 13 }}>
              {trip.matchOpponent ?? trip.sport ?? "—"}
            </span>
          </div>
          <Field label={t("worklist.owner")}>
            <Select
              value={ownerUserId}
              onChange={setOwnerUserId}
              options={[
                { value: UNASSIGNED, label: t("worklist.unassigned") },
                ...owners.map((o) => ({ value: o.id, label: o.name })),
              ]}
            />
          </Field>
        </div>
      </div>
      <div className="modal-foot">
        <Button type="button" variant="ghost" onClick={onClose}>
          {t("actions.cancel")}
        </Button>
        <Button type="button" onClick={onApply} disabled={saving}>
          {t("worklist.assign")}
        </Button>
      </div>
    </Modal>
  );
}
